import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './styles.css';

/**
 * Extract task ID from a dependency (handles both string and object format)
 */
const getDependencyTaskId = (dep) => {
  if (typeof dep === 'string') return dep;
  if (dep && typeof dep === 'object') return dep.taskId;
  return null;
};

/**
 * Extract branch from a dependency
 */
const getDependencyBranch = (dep) => {
  if (dep && typeof dep === 'object') return dep.branch || null;
  return null;
};

/**
 * Get dependencies for a task with full info
 */
const getDependencies = (task, allTasks) => {
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : (task.dependsOn ? [task.dependsOn] : []);
  return deps.map(dep => {
    const taskId = getDependencyTaskId(dep);
    const branch = getDependencyBranch(dep);
    const depTask = allTasks.find(t => t.id === taskId);
    
    if (!depTask) return null;
    
    let branchLabel = null;
    let branchColor = null;
    if (depTask.nodeType === 'decision' && branch) {
      const options = depTask.options || [
        { id: 'yes', label: 'Yes', color: '#10b981' },
        { id: 'no', label: 'No', color: '#ef4444' },
      ];
      const option = options.find(opt => opt.id === branch);
      if (option) {
        branchLabel = option.label;
        branchColor = option.color;
      }
    }
    
    return { task: depTask, branch, branchLabel, branchColor };
  }).filter(Boolean);
};

/**
 * Get available dependencies for a task
 */
const getAvailableDependencies = (taskId, allTasks) => {
  return allTasks.filter(t => {
    if (t.id === taskId) return false;
    const otherDeps = Array.isArray(t.dependsOn) ? t.dependsOn : (t.dependsOn ? [t.dependsOn] : []);
    return !otherDeps.some(dep => getDependencyTaskId(dep) === taskId);
  });
};

/**
 * Get current dependency task IDs
 */
const getCurrentDepIds = (task) => {
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : (task.dependsOn ? [task.dependsOn] : []);
  return deps.map(dep => getDependencyTaskId(dep)).filter(Boolean);
};

/**
 * Toggle a dependency on a task
 */
const toggleDependency = (task, depTaskId) => {
  const currentDeps = Array.isArray(task.dependsOn) ? task.dependsOn : (task.dependsOn ? [task.dependsOn] : []);
  const currentDepIds = currentDeps.map(dep => getDependencyTaskId(dep));
  
  if (currentDepIds.includes(depTaskId)) {
    return currentDeps.filter(dep => getDependencyTaskId(dep) !== depTaskId);
  } else {
    return [...currentDeps, depTaskId];
  }
};

/**
 * DependencyDropdown Component
 * 
 * Shared dropdown for selecting task dependencies.
 * Used by TableView cells and card indicators.
 * 
 * Features:
 * - Search box for filtering tasks
 * - Multi-select without closing
 * - Shows selected count
 * - Task type indicators (mission vs decision)
 */
const DependencyDropdown = ({
  task,
  allTasks = [],
  onDependencyChange,
  isOpen,
  onClose,
  anchorRef,
  position: customPosition,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get dependency info
  const dependencies = useMemo(() => getDependencies(task, allTasks), [task, allTasks]);
  const availableDeps = useMemo(() => getAvailableDependencies(task.id, allTasks), [task.id, allTasks]);
  const currentDepIds = useMemo(() => new Set(getCurrentDepIds(task)), [task]);

  // Filter available deps by search query
  const filteredDeps = useMemo(() => {
    if (!searchQuery.trim()) return availableDeps;
    const query = searchQuery.toLowerCase();
    return availableDeps.filter(dep => 
      dep.name.toLowerCase().includes(query)
    );
  }, [availableDeps, searchQuery]);

  // Separate selected and unselected
  const selectedDeps = useMemo(() => 
    filteredDeps.filter(dep => currentDepIds.has(dep.id)),
    [filteredDeps, currentDepIds]
  );
  const unselectedDeps = useMemo(() => 
    filteredDeps.filter(dep => !currentDepIds.has(dep.id)),
    [filteredDeps, currentDepIds]
  );

  // Calculate position from anchor - update function
  const updatePosition = () => {
    if (anchorRef?.current && !customPosition) {
      const rect = anchorRef.current.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - 320);
      setDropdownPosition({
        top: rect.bottom + 4,
        left: Math.max(8, left),
      });
    } else if (customPosition) {
      setDropdownPosition(customPosition);
    }
  };

  // Calculate position on open and when scrolling
  useEffect(() => {
    if (!isOpen) return;
    
    updatePosition();

    // Listen for scroll events on any scrollable parent to reposition
    const handleScroll = () => {
      updatePosition();
    };

    // Add scroll listener to window and any scrollable containers
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, anchorRef, customPosition]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        (!anchorRef?.current || !anchorRef.current.contains(e.target))
      ) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, anchorRef, onClose]);

  const handleToggleDependency = (depTaskId) => {
    const newDeps = toggleDependency(task, depTaskId);
    onDependencyChange?.(task.id, newDeps);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose?.();
    }
  };

  const selectedCount = currentDepIds.size;

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="dependency-dropdown"
      style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header with count */}
      <div className="dependency-dropdown__header">
        <span className="dependency-dropdown__title">
          What needs to happen first?
        </span>
        {selectedCount > 0 && (
          <span className="dependency-dropdown__count">
            {selectedCount} blocker{selectedCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Search box */}
      <div className="dependency-dropdown__search-wrapper">
        <input
          ref={searchInputRef}
          type="text"
          className="dependency-dropdown__search-input"
          placeholder="Search for tasks that block this one..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        {searchQuery && (
          <button 
            className="dependency-dropdown__search-clear"
            onClick={() => setSearchQuery('')}
            type="button"
          >
            ×
          </button>
        )}
      </div>

      {/* Task list */}
      <div className="dependency-dropdown__list-container">
        {/* Selected tasks first */}
        {selectedDeps.length > 0 && (
          <>
            <div className="dependency-dropdown__section-label">Must finish first</div>
            {selectedDeps.map((dep) => (
              <label key={dep.id} className="dependency-dropdown__checkbox dependency-dropdown__checkbox--selected">
                <input
                  type="checkbox"
                  checked={true}
                  onChange={() => handleToggleDependency(dep.id)}
                />
                <span className="dependency-dropdown__checkbox-custom" />
                <span className="dependency-dropdown__item-content">
                  <span className="dependency-dropdown__item-name">{dep.name}</span>
                  {dep.nodeType === 'decision' && (
                    <span className="dependency-dropdown__item-type dependency-dropdown__item-type--decision">
                      Decision
                    </span>
                  )}
                </span>
              </label>
            ))}
          </>
        )}

        {/* Available tasks */}
        {unselectedDeps.length > 0 && (
          <>
            {selectedDeps.length > 0 && (
              <div className="dependency-dropdown__section-label">Other tasks</div>
            )}
            {unselectedDeps.map((dep) => (
              <label key={dep.id} className="dependency-dropdown__checkbox">
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => handleToggleDependency(dep.id)}
                />
                <span className="dependency-dropdown__checkbox-custom" />
                <span className="dependency-dropdown__item-content">
                  <span className="dependency-dropdown__item-name">{dep.name}</span>
                  {dep.nodeType === 'decision' && (
                    <span className="dependency-dropdown__item-type dependency-dropdown__item-type--decision">
                      Decision
                    </span>
                  )}
                </span>
              </label>
            ))}
          </>
        )}

        {/* Empty states */}
        {filteredDeps.length === 0 && searchQuery && (
          <div className="dependency-dropdown__empty">
            No tasks matching "{searchQuery}"
          </div>
        )}
        {availableDeps.length === 0 && (
          <div className="dependency-dropdown__empty">
            No other tasks in this campaign
          </div>
        )}
      </div>

      {/* Footer hint */}
      {availableDeps.length > 5 && !searchQuery && (
        <div className="dependency-dropdown__hint">
          Type to find tasks among {availableDeps.length} available
        </div>
      )}
    </div>,
    document.body
  );
};

// Also export the helper functions for use by other components
export { getDependencies, getAvailableDependencies, getCurrentDepIds, toggleDependency };

export default DependencyDropdown;
